import { AppColors } from "@/styles/colors";
import {
  Button,
  CloseButton,
  Dialog,
  Flex,
  MenuOpenChangeDetails,
  Portal,
  Text,
} from "@chakra-ui/react";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { LightMode } from "../ui/color-mode";
import { SelectComponent } from "../select/Select";
import { FieldValues, useForm, useWatch } from "react-hook-form";
import { TextArea } from "../text-area/TextArea";
import { getMotivos } from "@/services/aux-entities.service";
import { usePolling } from "@/hooks/usePolling";

interface Props {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  handleAccept?: (event?: FieldValues) => Promise<void>;
  loadingAccept?: boolean;
  idTipoSolicitud?: number;
  isCancelling?: boolean;
  entityName?: string
}

export const RejectRequestModal = ({
  open,
  isCancelling = false,
  setOpen,
  handleAccept,
  loadingAccept,
  idTipoSolicitud = 1,
  entityName = "solicitud"
}: Props) => {
  const [isActive, setIsActive] = useState<boolean>(false);

  const [loadingMotivos, setLoadingMotivos] = useState<boolean>(false);

  const [motivos, setMotivos] = useState<any[]>([]);

  const fetchMotivos = async () => {
    setLoadingMotivos(true);

    const response = await getMotivos({
      esRechazo: true,
      idTipoSolicitud,
    });

    setMotivos(response ?? []);

    setLoadingMotivos(false);
  };

  useEffect(() => {
    setIsActive(true);
  }, []);

  useEffect(() => {
    if (isActive) {
      fetchMotivos();
    }
  }, [isActive]);

  usePolling({
    callback: () => fetchMotivos(),
    delay: 10000,
    isActive,
  });

  const {
    control,
    register,
    reset,
    formState: { errors },
    getValues,
  } = useForm();

  const message_motive = useWatch({
    name: "message_motive",
    control,
  });

  const messageLabel = motivos?.find(
    (el) => el.id === message_motive?.[0],
  )?.descripcion;

  const handleSetOpen = (e: MenuOpenChangeDetails) => {
    reset({
      message_motive: [],
      reject_motive: undefined,
    });
    setOpen(e.open);
  };

  const submitForm = async () => {
    await handleAccept?.({
      ...getValues(),
      request_detail:
        messageLabel === "Otro" ? getValues().reject_motive : messageLabel,
    });

    reset({
      message_motive: [],
      reject_motive: "",
    });
  };

  return (
    <Dialog.Root
      placement="center"
      lazyMount
      open={open}
      onOpenChange={handleSetOpen}
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <LightMode>
            <Dialog.Content data-testid="confirm-modal">
              <Dialog.Header paddingY="16px" paddingLeft="24px">
                <Dialog.Title
                  fontWeight={600}
                  color={AppColors.GRAY_TEXT}
                  fontSize="18px"
                >
                  {isCancelling ? "Cancelar" : "Rechazar"} {entityName}
                </Dialog.Title>
              </Dialog.Header>
              <Dialog.Body paddingX="24px" paddingY="8px" width="100%">
                <Flex flexDir="column" gap="10px">
                  <Text fontSize="16px" color={AppColors.GRAY_TEXT}>
                    Seleccione la razón{" "}
                    {isCancelling ? "de la cancelación" : "del rechazo"}
                  </Text>

                  <SelectComponent
                    control={control}
                    name="message_motive"
                    loading={loadingMotivos}
                    placeholder="Seleccione una opción"
                    items={motivos?.map((motivo) => ({
                      label: motivo?.descripcion,
                      value: motivo?.id,
                    }))}
                    fullWidth
                  />

                  {/* Ver como mapear esto */}
                  {messageLabel === "Otro" && (
                    <TextArea
                      errors={errors}
                      inputName="reject_motive"
                      register={register}
                      placeholder={`Ingresar el motivo ${isCancelling ? "de la cancelación" : "del rechazo"}`}
                      maxLength={50}
                    />
                  )}
                  <Text fontSize="16px" color={AppColors.GRAY_TEXT}>
                    ¿Desea {isCancelling ? "cancelar" : "rechazar"} la 
                    {" " + entityName}? No podrás deshacer esta acción después.
                  </Text>
                </Flex>
              </Dialog.Body>
              <Dialog.Footer paddingY="16px" paddingX="24px">
                <Dialog.ActionTrigger asChild>
                  <Button variant="outline" paddingX="16px" fontWeight={600}>
                    Cancelar
                  </Button>
                </Dialog.ActionTrigger>
                <Button
                  paddingX="16px"
                  bgColor={AppColors.GREEN_PRIMARY}
                  fontWeight={600}
                  onClick={submitForm}
                  loading={loadingAccept}
                  disabled={loadingAccept || !message_motive?.length}
                >
                  Aceptar
                </Button>
              </Dialog.Footer>
              <Dialog.CloseTrigger asChild>
                <CloseButton size="sm" />
              </Dialog.CloseTrigger>
            </Dialog.Content>
          </LightMode>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};
